import {
  invalidateWorkspaceRead,
  readWorkspaceCached,
  type WorkspaceServerService,
} from '@affine/core/modules/cloud';
import {
  getWorkspaceConfigQuery,
  getWorkspaceInviteLinkQuery,
  setAppBasesEnabledMutation,
  setEnableAiMutation,
  setEnableUrlPreviewMutation,
  setPresenceDiscoveryEnabledMutation,
} from '@affine/graphql';
import { Store } from '@toeverything/infra';

export class WorkspaceShareSettingStore extends Store {
  constructor(private readonly workspaceServerService: WorkspaceServerService) {
    super();
  }

  async fetchWorkspaceConfig(workspaceId: string, signal?: AbortSignal) {
    const server = this.workspaceServerService.server;
    if (!server) {
      throw new Error('No Server');
    }
    const data = await readWorkspaceCached({
      workspaceId,
      key: 'workspace-config',
      signal,
      load: () =>
        server.gql({
          query: getWorkspaceConfigQuery,
          variables: { id: workspaceId },
        }),
    });
    return data.workspace;
  }

  async fetchPresenceDiscoveryPolicy(
    workspaceId: string,
    signal?: AbortSignal
  ) {
    const server = this.workspaceServerService.server;
    if (!server) return false;
    const result = await server.gql({
      query: getWorkspaceConfigQuery,
      variables: { id: workspaceId },
      context: { signal },
    });
    return result.workspace.presenceDiscoveryEnabled;
  }

  async updatePresenceDiscovery(workspaceId: string, enabled: boolean) {
    const server = this.workspaceServerService.server;
    if (!server) throw new Error('No Server');
    const result = await server.gql({
      query: setPresenceDiscoveryEnabledMutation,
      variables: { id: workspaceId, enabled },
    });
    invalidateWorkspaceRead(workspaceId, 'workspace-config');
    return result.updateWorkspace.presenceDiscoveryEnabled;
  }

  async fetchWorkspaceInviteLink(workspaceId: string, signal?: AbortSignal) {
    const server = this.workspaceServerService.server;
    if (!server) {
      throw new Error('No Server');
    }
    const data = await server.gql({
      query: getWorkspaceInviteLinkQuery,
      variables: { id: workspaceId },
      context: { signal },
    });
    return data.workspace.inviteLink;
  }

  async updateWorkspaceEnableAi(
    workspaceId: string,
    enableAi: boolean,
    signal?: AbortSignal
  ) {
    if (!this.workspaceServerService.server) {
      throw new Error('No Server');
    }
    await this.workspaceServerService.server.gql({
      query: setEnableAiMutation,
      variables: {
        id: workspaceId,
        enableAi,
      },
      context: {
        signal,
      },
    });
    invalidateWorkspaceRead(workspaceId, 'workspace-config');
  }

  async updateWorkspaceEnableUrlPreview(
    workspaceId: string,
    enableUrlPreview: boolean,
    signal?: AbortSignal
  ) {
    if (!this.workspaceServerService.server) {
      throw new Error('No Server');
    }
    await this.workspaceServerService.server.gql({
      query: setEnableUrlPreviewMutation,
      variables: {
        id: workspaceId,
        enableUrlPreview,
      },
      context: {
        signal,
      },
    });
    invalidateWorkspaceRead(workspaceId, 'workspace-config');
  }

  // sl-xm1c.2.4: Workspace-Admin Toggle for the AppBases feature
  async updateWorkspaceAppBasesEnabled(
    workspaceId: string,
    appBasesEnabled: boolean,
    signal?: AbortSignal
  ) {
    if (!this.workspaceServerService.server) {
      throw new Error('No Server');
    }
    await this.workspaceServerService.server.gql({
      query: setAppBasesEnabledMutation,
      variables: {
        id: workspaceId,
        appBasesEnabled,
      },
      context: {
        signal,
      },
    });
    invalidateWorkspaceRead(workspaceId, 'workspace-config');
  }
}
